/* Split Accordion — Standalone JS (no theme dependencies)
   Self-contained IIFE. Handles accordion open/close, image swapping,
   and responds to Shopify theme editor section events.
*/
(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 750;

  class SplitAccordion {
    constructor(el) {
      this.el = el;
      this.trigger = el.dataset.trigger || 'click';
      this.openState = el.dataset.openState || 'first';
      this.mobileOpenState = el.dataset.mobileOpenState || 'inherit';
      this.isFixed = el.classList.contains('sa--fixed-height');
      this.items = Array.from(el.querySelectorAll('.sa__item'));
      this.images = Array.from(el.querySelectorAll('.sa__main-image'));
      this.activeItem = null;
      this._resizeHandler = null;
      this._boundHandlers = new Map();
      this._wasMobile = this._isMobile;

      this._init();
      this._bindEvents();
    }

    /* ----------------------------------------------------------
       Helpers
    ---------------------------------------------------------- */

    get _isMobile() {
      return window.innerWidth < MOBILE_BREAKPOINT;
    }

    /** Returns true when the max-height animation strategy should be used */
    get _useMaxHeight() {
      return !this.isFixed || this._isMobile;
    }

    /** Open state in effect at the current breakpoint */
    get _openState() {
      if (this._isMobile && this.mobileOpenState !== 'inherit') {
        return this.mobileOpenState;
      }
      return this.openState;
    }

    /** "All open" is a static list at this breakpoint — nothing is clickable */
    get allowMultiple() {
      return this._openState === 'all';
    }

    /* ----------------------------------------------------------
       Core actions
    ---------------------------------------------------------- */

    _open(item) {
      if (this.activeItem === item && !this.allowMultiple) return;

      if (!this.allowMultiple) this._closeAll(false);
      item.classList.add('is-active');
      this.activeItem = item;

      if (this._useMaxHeight) {
        const body = item.querySelector('.sa__item-body');
        if (body) {
          // Force a reflow so the transition starts from 0
          body.style.maxHeight = body.scrollHeight + 'px';
        }
      }

      const changeImage = item.dataset.changeImage === 'true';
      this._showImage(changeImage ? item.dataset.imageBlock : 'initial');
    }

    _close(item) {
      item.classList.remove('is-active');
      if (this._useMaxHeight) {
        const body = item.querySelector('.sa__item-body');
        if (body) body.style.maxHeight = '0';
      }
    }

    _closeAll(resetImage = true) {
      this.items.forEach(i => this._close(i));
      this.activeItem = null;
      if (resetImage) this._showImage('initial');
    }

    _toggle(item) {
      /* Static at this breakpoint — ignore the interaction */
      if (this.allowMultiple) return;

      if (item.classList.contains('is-active')) {
        this._close(item);
        this.activeItem = null;
        this._showImage('initial');
      } else {
        this._open(item);
      }
    }

    _showImage(blockId) {
      const hasMatch = this.images.some(img => img.dataset.block === blockId);
      const targetId = hasMatch ? blockId : 'initial';
      this.images.forEach(img => {
        img.classList.toggle('is-active', img.dataset.block === targetId);
      });
    }

    /* ----------------------------------------------------------
       Event binding
    ---------------------------------------------------------- */

    _bindEvents() {
      /* Handlers are always bound: the section can be static on one breakpoint
         and interactive on the other. _toggle/_open no-op when static. */
      this.items.forEach(item => {
        const header = item.querySelector('.sa__item-header');
        if (!header) return;

        if (this.trigger === 'hover') {
          const handler = () => {
            if (this.allowMultiple) return;
            this._open(item);
          };
          this._boundHandlers.set(header, handler);
          header.addEventListener('mouseenter', handler);
        } else {
          const handler = () => this._toggle(item);
          this._boundHandlers.set(header, handler);
          header.addEventListener('click', handler);
        }
      });

      /* On hover mode: when the section loses focus, restore the first item */
      if (this.trigger === 'hover' && this.items[0]) {
        const firstItem = this.items[0];
        const leaveHandler = () => {
          if (this.allowMultiple || this._openState !== 'first') return;
          this._open(firstItem);
        };
        this._boundHandlers.set(this.el, leaveHandler);
        this.el.addEventListener('mouseleave', leaveHandler);
      }

      this._resizeHandler = () => {
        /* Crossing the breakpoint can change the open state and the animation
           strategy — rebuild from scratch instead of patching heights */
        const isMobile = this._isMobile;
        if (isMobile !== this._wasMobile) {
          this._wasMobile = isMobile;
          this._applyState();
          return;
        }

        /* Same breakpoint: just re-measure the open bodies (font/layout changes) */
        if (!this._useMaxHeight) return;
        this.items
          .filter(i => i.classList.contains('is-active'))
          .forEach(i => {
            const body = i.querySelector('.sa__item-body');
            if (body) body.style.maxHeight = body.scrollHeight + 'px';
          });
      };
      window.addEventListener('resize', this._resizeHandler, { passive: true });
    }

    /* ----------------------------------------------------------
       Initialisation
    ---------------------------------------------------------- */

    _init() {
      this._applyState();
    }

    /** Resets every item and re-opens according to the current breakpoint's state */
    _applyState() {
      const state = this._openState;
      const useMaxHeight = this._useMaxHeight;

      this.items.forEach(item => {
        item.classList.remove('is-active');
        const body = item.querySelector('.sa__item-body');
        if (!body) return;
        /* Collapsed in max-height mode; inline value cleared in flex-grow mode */
        body.style.maxHeight = useMaxHeight ? '0' : '';
      });
      this.activeItem = null;

      if (state === 'all') {
        this.items.forEach(item => this._open(item));
        /* With several items open the main image is ambiguous — show the initial */
        this._showImage('initial');
      } else if (state === 'first' && this.items[0]) {
        this._open(this.items[0]);
      } else {
        this._showImage('initial');
      }
    }

    /* ----------------------------------------------------------
       Cleanup (called on section unload in theme editor)
    ---------------------------------------------------------- */

    destroy() {
      if (this._resizeHandler) {
        window.removeEventListener('resize', this._resizeHandler);
      }

      this._boundHandlers.forEach((handler, target) => {
        if (target.tagName === 'DIV' && target.classList.contains('sa__item-header')) {
          target.removeEventListener('mouseenter', handler);
          target.removeEventListener('click', handler);
        } else {
          target.removeEventListener('mouseleave', handler);
        }
      });

      this._boundHandlers.clear();
    }
  }

  /* ----------------------------------------------------------
     Bootstrap
  ---------------------------------------------------------- */

  function initAll(root) {
    const scope = root || document;
    scope.querySelectorAll('.sa[data-sa-init]').forEach(el => {
      if (!el._splitAccordion) {
        el._splitAccordion = new SplitAccordion(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  /* Shopify theme editor lifecycle */
  document.addEventListener('shopify:section:load', e => {
    const el = e.target.querySelector('.sa[data-sa-init]');
    if (el) {
      if (el._splitAccordion) el._splitAccordion.destroy();
      el._splitAccordion = new SplitAccordion(el);
    }
  });

  document.addEventListener('shopify:section:unload', e => {
    const el = e.target.querySelector('.sa[data-sa-init]');
    if (el && el._splitAccordion) {
      el._splitAccordion.destroy();
      el._splitAccordion = null;
    }
  });

})();
